import { US_STATES } from '@bonsai/shared';

export interface AddressSuggestion {
  id: string;
  /** The whole address on one line, as shown in the suggestion list. */
  label: string;
  addressLine1: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface ContextPart {
  name?: string;
  region_code?: string;
  country_code?: string;
}

interface MapboxFeature {
  properties: {
    mapbox_id: string;
    name?: string;
    place_formatted?: string;
    context?: {
      postcode?: ContextPart;
      place?: ContextPart;
      locality?: ContextPart;
      region?: ContextPart;
      country?: ContextPart;
    };
  };
}

const MAPBOX_FORWARD_URL = 'https://api.mapbox.com/search/geocode/v6/forward';
const SUGGESTION_LIMIT = 5;
export const MIN_QUERY_LENGTH = 3;

const OFFERED_STATES: ReadonlySet<string> = new Set(US_STATES);

function mapboxToken(): string | undefined {
  const token = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;
  return token?.trim() || undefined;
}

export function addressSuggestionsAvailable(): boolean {
  return Boolean(mapboxToken());
}

type FeatureContext = NonNullable<MapboxFeature['properties']['context']>;

function cityOf(ctx: FeatureContext): string {
  return ctx.place?.name ?? ctx.locality?.name ?? '';
}

function stateOf(ctx: FeatureContext): string {
  return ctx.region?.region_code?.toUpperCase() ?? '';
}

function toSuggestion({ properties: p }: MapboxFeature): AddressSuggestion | null {
  const ctx = p.context ?? {};
  const [city, state] = [cityOf(ctx), stateOf(ctx)];
  if (!p.name || !city || !OFFERED_STATES.has(state)) return null;
  return {
    id: p.mapbox_id,
    label: [p.name, p.place_formatted].filter(Boolean).join(', '),
    addressLine1: p.name,
    city,
    state,
    zipCode: ctx.postcode?.name ?? '',
    country: ctx.country?.country_code?.toUpperCase() ?? 'US',
  };
}

/** US street addresses that start with what was typed. */
export async function fetchAddressSuggestions(
  query: string,
  signal?: AbortSignal,
): Promise<AddressSuggestion[]> {
  const token = mapboxToken();
  if (!token) return [];
  const url = new URL(MAPBOX_FORWARD_URL);
  url.searchParams.set('q', query);
  url.searchParams.set('autocomplete', 'true');
  url.searchParams.set('types', 'address');
  url.searchParams.set('country', 'us');
  url.searchParams.set('limit', String(SUGGESTION_LIMIT));
  url.searchParams.set('access_token', token);

  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`Address lookup failed: ${res.status}`);
  const body = (await res.json()) as { features?: MapboxFeature[] };
  return (body.features ?? []).map(toSuggestion).filter((s): s is AddressSuggestion => s !== null);
}
